import FormControl from '@mui/material/FormControl'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import styled from 'styled-components'
import {Help} from '@mui/icons-material'
import Autocomplete from 'components/Autocomplete'
import Dialog from 'components/Dialog'
import SendingError from 'components/SendingError'
import TestMessage from 'components/TestMessage'
import React from 'react'
import {injectIntl} from 'react-intl'
import {getSmsSubstitutions} from 'utils/utils'
import periodicity from 'utils/enums/periodicity'
import {messageLinkTrue, messageLinkFalse} from 'utils/helpers'

const Row = styled.div`
    display: flex;
    margin-bottom: 20px;
`

const Field = styled(TextField)`
    width: calc(50% - 15px);
    margin-right: 30px;

    &:nth-child(2n) {
        margin-right: 0
    }
`

const Icon = styled(Help)`
	position: absolute;
	bottom: 0;
	right: 0;
	font-size: 30px;
	color: #fdc22d;
	cursor: pointer;
	margin-bottom: 10px;
`

class SmsTemplate extends React.Component {
    constructor(props) {
        super(props)

        this.substitutions = getSmsSubstitutions(props.entity)

        this.state = {
            name: props.template ? props.template.name : '',
            sender: props.template ? props.template.sender : '',
            body: props.template ? props.template.body : '',
            feedbackPeriodicity: props.template ? props.template.feedbackPeriodicity : periodicity.Month.value,
            open: false,
            field: null
        }
    }

    handleChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        })
    }

    handleCreate = () => {
        this.props.createSmsTemplate({
            entityId: this.props.entity,
            name: this.state.name,
            sender: this.state.sender,
            body: this.state.body,
            feedbackPeriodicity: this.state.feedbackPeriodicity,
            emissionPath: this.props.emission.pathId
        })
        this.props.handleClose()
    }

    handleUpdate = () => {
        this.props.updateSmsTemplate({
            id: this.props.template.id,
            entityId: this.props.entity,
            name: this.state.name,
            sender: this.state.sender,
            body: this.state.body,
            feedbackPeriodicity: this.state.feedbackPeriodicity,
            emissionPath: this.props.emission.pathId
        })
        this.props.handleClose()
    }

    handleDelete = () => {
        this.props.deleteSmsTemplate(this.props.template.id)
        this.props.handleClose()
    }

    handleSend = (save, address = '') => {
        if (this.props.template) {
            this.props.sendSmsTemplate({
                id: this.props.template.id,
                entityId: this.props.entity,
                name: this.state.name,
                sender: this.state.sender,
                body: this.state.body,
                feedbackPeriodicity: this.state.feedbackPeriodicity,
                emissionPath: this.props.emission.pathId
            }, save, address)
        } else {
            this.props.sendSmsTemplate({
                entityId: this.props.entity,
                name: this.state.name,
                sender: this.state.sender,
                body: this.state.body,
                feedbackPeriodicity: this.state.feedbackPeriodicity,
                emissionPath: this.props.emission.pathId
            }, save, address)
        }
    }

    handleOpen = () => {
        this.setState({
            open: true,
            field: this.props.fields[0].id
        })
    }

    handleClose = (e) => {
        this.setState({
            open: false,
            field: null
        })
    }

    handleUpdateMessage = () => {
        let originalCursor, substring = `${this.props.intl.formatMessage({id: "sidebar.positive_link"})} [${messageLinkTrue}](${this.state.field})\n${this.props.intl.formatMessage({id: "sidebar.negative_link"})} [${messageLinkFalse}](${this.state.field})`
        const body = document.getElementsByClassName('autocomplete')[0]
        const text = body.value
        const cursor = body.selectionStart
        originalCursor = cursor
        const originalRange = substring.length

        if (cursor && text.substring(cursor - 1, cursor) !== '\n') {
            substring = '\n' + substring
            originalCursor = originalCursor + 1
        }

        if (cursor !== text.length && text.substring(cursor, cursor + 1) !== '\n') {
            substring = substring + '\n'
        }

        const value = text.substring(0, cursor) + substring + text.substring(cursor, text.length)

        this.setState({
            body: value,
            open: false,
            field: null
        }, () => {
            body.focus()
            body.setSelectionRange(originalCursor, originalCursor + originalRange)
        })
    }

    handleSendTest = (address) => {
        this.handleSend(false, address)
    }

    handleReset = () => {
        if (!this.props.test) {
            this.props.handleClose()
        }
        
        this.props.handleReset()
    }

    render() {
        const {intl} = this.props
        const validation = !this.state.name || !this.state.sender || !this.state.body

        return (
            <React.Fragment>
                <DialogTitle>
                    {this.props.template ?
                        intl.formatMessage({id: "sidebar.send_to"}, {
                            name: this.props.template.name, to: this.props.emission.pathName})
                        :
                        intl.formatMessage({id: "sidebar.send_to"}, {
                            name: '', to: this.props.emission.pathName})
                    }
                </DialogTitle>
                <DialogContent sx={{overflowY: 'visible'}}>
                    <Row>
                        <Field variant="standard"
                            value={this.state.name}
                            onChange={this.handleChange}
                            label={intl.formatMessage({id: "sidebar.template_name"})}
                            InputLabelProps={{
                                shrink: true
                            }}
                            inputProps={{
                                maxLength: global.NAME_MAX_LENGTH,
                                name: 'name'
                            }}
                        />
                        <Field variant="standard"
                            value={this.state.sender}
                            onChange={this.handleChange}
                            label={intl.formatMessage({id: "sidebar.sms_sender"})}
                            InputLabelProps={{
                                shrink: true
                            }}
                            inputProps={{
                                maxLength: global.NAME_MAX_LENGTH,
                                name: 'sender'
                            }}
                        />
                    </Row>
                    <Row>
                        <FormControl sx={{width: 'calc(50% - 15px)'}} variant="standard">
                            <InputLabel>
                                {intl.formatMessage({id: "editor.feedback_periodicity"})}
                            </InputLabel>
                            <Select
                                value={this.state.feedbackPeriodicity}
                                onChange={this.handleChange}
                                inputProps={{
                                    name: 'feedbackPeriodicity'
                                }}
                            >
                                {Object.keys(periodicity).map(item =>
                                    <MenuItem key={periodicity[item].value} value={periodicity[item].value}>
                                        {intl.formatMessage({id: `editor.periodicity_${periodicity[item].label}`})}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Row>
                    <div style={{position: 'relative'}}>
                        <Autocomplete
                            style={{height: '150px'}}
                            value={this.state.body}
                            label={intl.formatMessage({id: "sidebar.sms_message"})}
                            onChange={(value) => this.setState({body: value})}
                            options={this.substitutions.map(field => field.name)}
                            component='textarea'
                        />
                        {!!this.props.fields.length &&
                            <Tooltip
                                title={intl.formatMessage({id: "sidebar.add_question"})}
                                placement="top"
                            >
                                <Icon onClick={this.handleOpen} />
                            </Tooltip>
                        }
                        {this.state.open &&
                            <Dialog
                                open={this.state.open}
                                onClose={this.handleClose}
                            >
                                <DialogTitle>
                                    Question creation
                                </DialogTitle>
                                <DialogContent>
                                    <Typography variant="body2">
                                        {intl.formatMessage({id: "sidebar.question_desc"})}
                                    </Typography>
                                    <FormControl style={{width: '100%', marginTop: '20px'}} variant="standard">
                                        <InputLabel>
                                            {intl.formatMessage({id: "sidebar.question_field"})}
                                        </InputLabel>
                                        <Select
                                            value={this.state.field}
                                            onChange={this.handleChange}
                                            inputProps={{
                                                name: 'field'
                                            }}
                                        >
                                            {this.props.fields.map(item =>
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.singular}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={this.handleClose} color="primary">
                                        {intl.formatMessage({id: "app.confirm_cancel"})}
                                    </Button>
                                    <Button variant="contained"
                                        onClick={this.handleUpdateMessage}
                                        color="primary"
                                    >
                                        {intl.formatMessage({id: "app.confirm_add"})}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        }
                    </div>
                    {this.props.sending &&
                        <TestMessage
                            type='sms'
                            handleSend={this.handleSendTest}
                            title={this.props.template ?
                                {name: this.props.template.name, to: this.props.emission.pathName} :
                                {name: '', to: this.props.emission.pathName}
                            }
                        />
                    }
                    {this.props.errors &&
                        <SendingError
                            successes={this.props.successes}
                            errors={this.props.errors}
                            handleClose={this.handleReset}
                            // handleReset={this.props.handleReset}
                            title={this.props.template ?
                                {name: this.props.template.name, to: this.props.emission.pathName} :
                                {name: '', to: this.props.emission.pathName}
                            }
                        />
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.props.handleClose} color="primary">
                        {intl.formatMessage({id: "app.confirm_cancel"})}
                    </Button>
                    {this.props.template &&
                        <Button variant="contained"
                            onClick={this.handleDelete}
                            color="secondary"
                        >
                            {intl.formatMessage({id: "app.confirm_delete"})}
                        </Button>
                    }
                    {this.props.sending &&
                        <Button variant="contained"
                            disabled={validation}
                            onClick={() => this.handleSend(false)}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_send"})}
                        </Button>
                    }
                    {this.props.template &&
                        <Button variant="contained"
                            disabled={validation}
                            onClick={this.handleUpdate}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_update"})}
                        </Button>
                    }
                    {!this.props.template &&
                        <Button variant="contained"
                            disabled={validation}
                            onClick={this.handleCreate}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_save"})}
                        </Button>
                    }
                    {this.props.template && this.props.sending &&
                        <Button variant="contained"
                            disabled={validation}
                            onClick={() => this.handleSend(true)}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_update_send"})}
                        </Button>
                    }
                    {!this.props.template && this.props.sending &&
                        <Button variant="contained"
                            disabled={validation}
                            onClick={() => this.handleSend(true)}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_save_send"})}
                        </Button>
                    }
                </DialogActions>
            </React.Fragment>
        )
    }
}

export default injectIntl(SmsTemplate)
